import type {
  DriverListItem,
  KartListItem,
  DataFileListItem,
  User,
  SavedTrack,
  ListDriversQuery,
  ListKartsQuery,
  ListDataFilesQuery,
} from './types/index.js';

export const GLOBAL_SEARCH_DEBOUNCE_MS = 180;
export const GLOBAL_SEARCH_CATALOG_MAX_AGE_MS = 60_000;

export interface PaddiqSearchResult {
  id: string;
  group: string;
  label: string;
  description?: string;
  path: string;
  kind: 'driver' | 'member' | 'kart' | 'session' | 'circuit';
}

export interface GlobalSearchDependencies {
  fetchDrivers: (query: ListDriversQuery) => Promise<{ data: DriverListItem[] }>;
  fetchKarts: (query: ListKartsQuery) => Promise<{ data: KartListItem[] }>;
  fetchDataFiles: (query: ListDataFilesQuery) => Promise<{ data: DataFileListItem[] }>;
  fetchTeamMembers: () => Promise<User[]>;
  fetchTelemetryTracks: () => Promise<SavedTrack[]>;
}

/** Shared result grouping, matching and deep links for web and Link search. */
export async function loadGlobalSearchResults(
  query: string,
  deps: GlobalSearchDependencies,
  isCurrent: () => boolean | Promise<boolean> = () => true,
): Promise<PaddiqSearchResult[]> {
  const normalizedQuery = query.toLowerCase();
  const [driversResult, kartsResult, sessionsResult, membersResult, tracksResult] =
    await Promise.allSettled([
      deps.fetchDrivers({ search: query, status: 'ALL', page: 1, perPage: 5 }),
      deps.fetchKarts({ search: query, status: 'ALL', page: 1, perPage: 5 }),
      deps.fetchDataFiles({
        search: query,
        source: 'ALL',
        detail: 'session-list',
        page: 1,
        perPage: 6,
      }),
      deps.fetchTeamMembers(),
      deps.fetchTelemetryTracks(),
    ]);
  if (
    [driversResult, kartsResult, sessionsResult, membersResult, tracksResult].every(
      (result) => result.status === 'rejected',
    )
  ) {
    throw new Error('Search is unavailable. Please try again.');
  }
  // A completed request must not start enrichment with another account's current token.
  if (!(await isCurrent())) return [];
  const results: PaddiqSearchResult[] = [];
  const tracksById = new Map(
    tracksResult.status === 'fulfilled' ? tracksResult.value.map((track) => [track.id, track]) : [],
  );
  const matchingTracks =
    tracksResult.status === 'fulfilled'
      ? tracksResult.value.filter((track) =>
          [track.name, track.address, track.locality, track.region, track.countryCode]
            .filter(Boolean)
            .join(' ')
            .toLowerCase()
            .includes(normalizedQuery),
        )
      : [];
  let matchingSessions = sessionsResult.status === 'fulfilled' ? sessionsResult.value.data : [];
  if (matchingTracks.length) {
    const sessionsAtMatchingTracks = await Promise.allSettled(
      matchingTracks.slice(0, 2).map((track) =>
        deps.fetchDataFiles({
          search: track.name,
          source: 'ALL',
          detail: 'session-list',
          page: 1,
          perPage: 6,
        }),
      ),
    );
    if (!(await isCurrent())) return [];
    matchingSessions = [
      ...matchingSessions,
      ...sessionsAtMatchingTracks.flatMap((result) =>
        result.status === 'fulfilled' ? result.value.data : [],
      ),
    ].filter(
      (session, index, sessions) =>
        sessions.findIndex((candidate) => candidate.id === session.id) === index,
    );
  }
  if (driversResult.status === 'fulfilled') {
    results.push(
      ...driversResult.value.data.map(
        (driver): PaddiqSearchResult => ({
          id: `driver:${driver.id}`,
          kind: 'driver',
          group: 'People',
          label: driver.name,
          description: [
            'Driver',
            driver.driverNumber ? `#${driver.driverNumber}` : null,
            driver.email,
          ]
            .filter(Boolean)
            .join(' · '),
          path: `/drivers/${encodeURIComponent(driver.id)}`,
        }),
      ),
    );
  }
  if (membersResult.status === 'fulfilled') {
    results.push(
      ...membersResult.value
        .filter((member) =>
          `${member.name} ${member.email} ${member.username ?? ''}`
            .toLowerCase()
            .includes(normalizedQuery),
        )
        .filter(
          (member) =>
            !results.some((result) => result.group === 'People' && result.label === member.name),
        )
        .slice(0, 5)
        .map(
          (member): PaddiqSearchResult => ({
            id: `member:${member.id}`,
            kind: 'member',
            group: 'People',
            label: member.name,
            description: `${member.role
              .toLowerCase()
              .split('_')
              .map((part) => part[0]!.toUpperCase() + part.slice(1))
              .join(' ')} · ${member.email}`,
            path: '/team-management',
          }),
        ),
    );
  }
  if (kartsResult.status === 'fulfilled') {
    results.push(
      ...kartsResult.value.data.map(
        (kart): PaddiqSearchResult => ({
          id: `kart:${kart.id}`,
          kind: 'kart',
          group: 'Karts',
          label: `#${kart.kartNumber}${kart.nickname ? ` · ${kart.nickname}` : ''}`,
          description:
            [kart.kartClass, kart.chassisBrand, kart.chassisModel].filter(Boolean).join(' · ') ||
            'Kart',
          path: `/karts?vehicle=${encodeURIComponent(kart.id)}`,
        }),
      ),
    );
  }
  results.push(
    ...matchingSessions.slice(0, 6).map((session): PaddiqSearchResult => {
      const recordedAt =
        session.summary.recordedAtMs ??
        (session.summary.sessionStartedAt ? Date.parse(session.summary.sessionStartedAt) : null);
      const assignedTrack = session.sessionContextSummary?.trackId
        ? tracksById.get(session.sessionContextSummary.trackId)
        : null;
      return {
        id: `session:${session.id}`,
        kind: 'session',
        group: 'Sessions',
        label: assignedTrack?.name || session.summary.trackName || session.originalName,
        description: [
          session.originalName,
          session.sessionAssignment?.driverName || session.summary.driverName,
          recordedAt
            ? new Date(recordedAt).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : null,
          session.summary.lapCount ? `${session.summary.lapCount} laps` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        path: `/sessions?view=review&session=${encodeURIComponent(session.id)}`,
      };
    }),
  );
  results.push(
    ...matchingTracks.slice(0, 6).map(
      (track): PaddiqSearchResult => ({
        id: `circuit:${track.id}`,
        kind: 'circuit',
        group: 'Circuits',
        label: track.name,
        description:
          [track.locality, track.region, track.countryCode].filter(Boolean).join(', ') ||
          track.address ||
          'Circuit',
        path: `/circuits/${encodeURIComponent(track.id)}`,
      }),
    ),
  );
  return results;
}
